import { Component, Input, Output, EventEmitter, OnInit, AfterViewInit, ChangeDetectorRef, ElementRef, ViewChild, OnChanges, SimpleChanges, HostListener, OnDestroy, effect, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { PropertyDataResponse, PropertyDataUpdate, LocationDataResponse, PropertyCodesResponse } from 'src/models/location-data.model';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { DropdownMenuItem } from '../dropdown-menu/dropdown-menu.component';
import { ActionDockButton } from '../action-dock/action-dock.component';
import { GlassCard } from '../glass-cards/glass-cards.component';
import { QuickActionEvent } from '../location-modal/location-modal.component';
import { WindowService } from 'src/shared/providers/window.service';
import { ChatDockService } from 'src/shared/services/chat-dock.service';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

export interface PulseEvent {
  date: string;
  title: string;
  type: 'past' | 'today' | 'future';
}

export interface CustomField {
  label: string;
  value: string;
}

export interface PropertyInfo {
  name: string;
  summary: string;
  nextJob: string;
  lastJob: string;
  openIssues: number;
  accessNotes?: string;
  unpaidBills?: number;
  lowInventory?: number;
  maintenanceDue?: number;
  listingsCount?: number;
  listingsPlatforms?: string[];
  customFields?: CustomField[];
  gettingStartedCompletedSteps?: number;
  gettingStartedTotalSteps?: number;
  showGettingStarted?: boolean;
  gettingStartedLoaded?: boolean;
}

@Component({
  selector: 'pulse-timeline',
  templateUrl: './pulse-timeline.component.html',
  styleUrls: ['./pulse-timeline.component.scss'],
  standalone: false
})
export class PulseTimelineComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('timelineScroll') timelineScrollRef!: ElementRef<HTMLElement>;
  @ViewChild('propertyPanelWrapper') propertyPanelRef!: ElementRef<HTMLElement>;

  @Output() goToEditPropertyPageClicked = new EventEmitter<void>();

  isTimelineHidden = true;
  private resizeObserver: ResizeObserver | null = null;

  @Input() set events(value: PulseEvent[]) {
    this._events = value || [];
    this.cdr.markForCheck();
    setTimeout(() => this.scrollToToday(), 100);
  }
  get events(): PulseEvent[] {
    return this._events;
  }
  private _events: PulseEvent[] = [];

  @Input() set propertyName(value: string) {
    this._propertyName = value?.trim() || '';
    this.cdr.markForCheck();
  }
  get propertyName(): string {
    return this._propertyName;
  }
  private _propertyName = '';

  @Input() forceEmptyState = false;

  @Input() propertyInfo: PropertyInfo = {
    name: '',
    summary: 'No recent issues reported. Property is in good standing.',
    nextJob: 'No upcoming jobs',
    lastJob: 'No past jobs',
    openIssues: 0,
    accessNotes: 'Lockbox code: 1234. Key under mat.',
    unpaidBills: 2,
    lowInventory: 3,
    maintenanceDue: 1,
    customFields: [
      { label: 'Hostaway Id', value: 'HA-12345' },
      { label: 'Owner Id', value: 'OWN-789' }
    ]
  };

  @Output() fieldClicked = new EventEmitter<string>();
  @Output() logJobClicked = new EventEmitter<void>();
  @Output() addJobClicked = new EventEmitter<void>();
  @Output() bookJobClicked = new EventEmitter<void>();
  @Output() setupSmartSuggestionsClicked = new EventEmitter<void>();

  @Input() isConsumer = false;
  @Input() selectedPropertyId: number | null = null;
  @Input() propertyItems: DropdownMenuItem[] = [];
  @Output() propertySelected = new EventEmitter<number | null>();
  @Output() addPropertyClicked = new EventEmitter<void>();
  @Input() propertyData: PropertyDataResponse | null = null;
  @Input() locationData: LocationDataResponse | null = null;
  @Input() propertyCodesData: PropertyCodesResponse | null = null;
  @Input() isLoadingPropertyData = false;
  @Input() isLoadingLocationData = false;
  @Input() isLoadingPropertyCodes = false;
  @Input() isLoadingPropertySummary = false;
  @Input() isTimelineLoaded = false;
  @Input() isLocationExpanded = false;
  @Output() toggleLocationExpand = new EventEmitter<void>();
  @Output() locationIconClicked = new EventEmitter<string>();
  @Output() quickActionClicked = new EventEmitter<{ category: string; action: string; initialMessage: string }>();
  @Input() viewModeItems: SelectModel[] = [];
  @Input() propertyViewMode: 'map' | 'floorplan' = 'map';
  @Output() viewModeChanged = new EventEmitter<'map' | 'floorplan'>();
  @Input() isGuestAccount = false;
  @Input() isRentalClient = false;
  @Output() signInClicked = new EventEmitter<void>();
  @Output() propertyDataChanged = new EventEmitter<PropertyDataUpdate>();

  private fb = inject(FormBuilder);
  private readonly demoBlock = inject(DemoBlockService);

  isEditingPropertyData = false;
  isSavingPropertyData = false;

  static integerValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') { return null; }
    return Number.isInteger(Number(value)) ? null : { integer: true };
  }

  propertyDataForm = this.fb.group({
    bedrooms: [null as number | null, [Validators.min(0), Validators.max(99), PulseTimelineComponent.integerValidator]],
    bathrooms: [null as number | null, [Validators.min(0), Validators.max(99), PulseTimelineComponent.integerValidator]],
    living_area_sqft: [null as number | null, [Validators.min(0), Validators.max(999999)]],
    lot_size_sqft: [null as number | null, [Validators.min(0), Validators.max(9999999)]],
    year_built: [null as number | null, [Validators.min(1800), Validators.max(new Date().getFullYear())]],
    property_type: [null as string | null],
  });

  propertyTypeItems: SelectModel[] = [
    { value: 'single_family', viewValue: 'Single Family' },
    { value: 'condo', viewValue: 'Condo' },
    { value: 'townhouse', viewValue: 'Townhouse' },
    { value: 'multi_family', viewValue: 'Multi-Family' },
  ];

  selectedLocationIcon: string | null = null;
  selectedLocationType: string | null = null;
  timelineMenuOpen = false;
  headerSelectItems: SelectModel[] = [];
  selectedHeaderValue = '';

  actionDockButtons: ActionDockButton[] = [
    { id: 'weather', icon: 'wb_sunny', hasData: false },
    { id: 'pests', icon: 'pest_control', hasData: false },
    { id: 'utilities', icon: 'bolt', hasData: false },
    { id: 'trash', icon: 'local_shipping', hasData: false },
    { id: 'ordinances', icon: 'gavel', hasData: false }
  ];

  get glassCards(): GlassCard[] {
    return [
      {
        id: 'nextJob',
        label: 'Next Job',
        value: this.propertyInfo.nextJob
      },
      {
        id: 'lastJob',
        label: 'Last Job',
        value: this.propertyInfo.lastJob
      },
      {
        id: 'openIssues',
        label: 'Open Issues',
        value: this.propertyInfo.openIssues,
        hasIssue: this.propertyInfo.openIssues > 0
      },
      {
        id: 'unpaidBills',
        label: 'Unpaid Bills',
        value: this.propertyInfo.unpaidBills || 0,
        hasIssue: (this.propertyInfo.unpaidBills || 0) > 0
      },
      {
        id: 'lowInventory',
        label: 'Low Inventory',
        value: this.propertyInfo.lowInventory || 0,
        hasWarning: (this.propertyInfo.lowInventory || 0) > 0
      },
      {
        id: 'maintenanceDue',
        label: 'Maintenance Due',
        value: (this.propertyInfo.maintenanceDue || 0) > 0 ? this.propertyInfo.maintenanceDue : 'No maintenance due',
        hasWarning: (this.propertyInfo.maintenanceDue || 0) > 0
      }
    ];
  }

  constructor(
    private cdr: ChangeDetectorRef,
    public windowService: WindowService,
    private chatDockService: ChatDockService
  ) {
    effect(() => {
      this.chatDockService.isEffectivelyDocked();
      this.checkTimelineOverlap();
    });
  }

  ngOnInit() {
    this.buildHeaderSelectItems();
    this.updateSelectedHeaderValue();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['propertyItems']) {
      this.buildHeaderSelectItems();
    }
    if (changes['selectedPropertyId']) {
      this.updateSelectedHeaderValue();
    }
    if (changes['isTimelineLoaded']) {
      this.checkTimelineOverlap();
    }
    if (changes['propertyData'] && !changes['propertyData'].isFirstChange()) {
      this.isSavingPropertyData = false;
      if (!(this.propertyData as any)?._saveError) {
        this.isEditingPropertyData = false;
        this.submitted = false;
      }
    }
  }

  private buildHeaderSelectItems(): void {
    this.headerSelectItems = this.propertyItems.map(item => ({
      value: item.value === null ? 'null' : String(item.value),
      viewValue: item.label
    }));
    this.headerSelectItems.push({ value: 'add-property', viewValue: `+ ${  new TranslationPipe().transform('Add Property')}` });
    this.updateSelectedHeaderValue();
  }

  private updateSelectedHeaderValue(): void {
    this.selectedHeaderValue = this.selectedPropertyId === null ? 'null' : String(this.selectedPropertyId);
  }

  onHeaderItemSelected(value: string) {
    if (value === 'add-property') {
      this.addPropertyClicked.emit();
    } else if (value === 'null') {
      this.propertySelected.emit(null);
    } else {
      this.propertySelected.emit(Number(value));
    }
  }

  ngAfterViewInit() {
    setTimeout(() => this.scrollToToday(), 150);
    this.setupOverlapDetection();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private setupOverlapDetection(): void {
    this.checkTimelineOverlap();
    this.resizeObserver = new ResizeObserver(() => this.checkTimelineOverlap());
    if (this.propertyPanelRef?.nativeElement) {
      this.resizeObserver.observe(this.propertyPanelRef.nativeElement);
    }
  }

  @HostListener('window:resize')
  checkTimelineOverlap(): void {
    if (!this.isTimelineLoaded) {
      this.isTimelineHidden = true;
      return;
    }
    if (!this.windowService.isDesktopRes) {
      this.isTimelineHidden = false;
      return;
    }
    const panelRect = this.propertyPanelRef?.nativeElement?.getBoundingClientRect();
    if (!panelRect) {
      this.isTimelineHidden = false;
      return;
    }
    const timelineWidth = 280;
    const gap = 40;
    const rightOffset = this.chatDockService.isEffectivelyDocked() ? 340 : 10;
    const timelineLeftEdge = window.innerWidth - rightOffset - timelineWidth;
    this.isTimelineHidden = (panelRect.right + gap) > timelineLeftEdge;
    this.cdr.markForCheck();
  }

  scrollToToday(): void {
    if (!this.timelineScrollRef?.nativeElement) { return; }
    const scrollContainer = this.timelineScrollRef.nativeElement;
    const todayElement = scrollContainer.querySelector('.is-today') as HTMLElement;
    if (todayElement) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const todayRect = todayElement.getBoundingClientRect();
      const scrollTop = todayElement.offsetTop - (containerRect.height / 2) + (todayRect.height / 2);
      scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  isToday(event: PulseEvent): boolean {
    return event.type === 'today';
  }

  isPast(event: PulseEvent): boolean {
    return event.type === 'past';
  }

  isFuture(event: PulseEvent): boolean {
    return event.type === 'future';
  }

  get listingsLabel(): string {
    const count = this.propertyInfo.listingsCount || 0;
    if (count === 0) { return 'No listings'; }
    return count === 1 ? '1 Listing' : `${count} Listings`;
  }

  get hasNoEvents(): boolean {
    return this.forceEmptyState || this._events.length === 0;
  }

  get hasPastEvents(): boolean {
    return this._events.some(e => e.type === 'past');
  }

  get hasFutureEvents(): boolean {
    return this._events.some(e => e.type === 'future');
  }

  onLogJob(): void {
    this.logJobClicked.emit();
    this.quickActionClicked.emit({
      category: 'timeline',
      action: 'log-job',
      initialMessage: `I need to log a job that was completed at ${this._propertyName}.`
    });
  }

  onAddJob(): void {
    this.addJobClicked.emit();
  }

  onBookJob(): void {
    if (this.demoBlock.block()) { return; }
    this.bookJobClicked.emit();
  }

  togglePropertyDataEdit(): void {
    this.isEditingPropertyData = !this.isEditingPropertyData;
    if (this.isEditingPropertyData) {
      const lookupType = this.propertyData?.data?.property_type;
      const isAcceptedType = this.propertyTypeItems.some((item) => item.value === lookupType);
      this.propertyDataForm.patchValue({
        bedrooms: this.propertyData?.data?.bedrooms ?? null,
        bathrooms: this.propertyData?.data?.bathrooms ?? null,
        living_area_sqft: this.propertyData?.data?.living_area_sqft ?? null,
        lot_size_sqft: this.propertyData?.data?.lot_size_sqft ?? null,
        year_built: this.propertyData?.data?.year_built ?? null,
        property_type: isAcceptedType ? lookupType : null,
      });
    }
  }

  submitted = false;

  onSavePropertyData = (): void => {
    this.submitted = true;
    if (this.propertyDataForm.invalid) {
      return;
    }
    const formValue = this.propertyDataForm.value;
    const toNumber = (val: any): number | null => (val !== null && val !== undefined && val !== '') ? Number(val) : null;
    const update: PropertyDataUpdate = {
      bedrooms: toNumber(formValue.bedrooms),
      bathrooms: toNumber(formValue.bathrooms),
      living_area_sqft: toNumber(formValue.living_area_sqft),
      lot_size_sqft: toNumber(formValue.lot_size_sqft),
      year_built: toNumber(formValue.year_built),
      property_type: (formValue.property_type as PropertyDataUpdate['property_type']) || null,
    };
    this.isSavingPropertyData = true;
    this.propertyDataChanged.emit(update);
  };

  onCancelPropertyDataEdit(): void {
    this.isEditingPropertyData = false;
  }

  goToEditPropertyPage() {
    if (this.demoBlock.block()) { return; }
    this.goToEditPropertyPageClicked.emit();
  }

  onSetupSmartSuggestions(): void {
    this.setupSmartSuggestionsClicked.emit();
    this.quickActionClicked.emit({
      category: 'timeline',
      action: 'smart-maintenance',
      initialMessage: `I want to set up smart maintenance suggestions for ${this._propertyName} to help prevent issues before they happen.`
    });
  }

  onFieldClick(field: string): void {
    // 'gettingStarted' is the demo onboarding CTA (it opens the Get Started / schedule-call
    // flow), so it must NOT be demo-blocked. Every other field is a real write action that
    // still shows the "create an account" prompt in the shared demo session.
    if (field !== 'gettingStarted' && this.demoBlock.block()) { return; }
    this.fieldClicked.emit(field);
  }

  onToggleLocationExpand(): void {
    this.toggleLocationExpand.emit();
  }

  onLocationIconClick(iconType: string): void {
    if (this.selectedLocationIcon === iconType) {
      this.selectedLocationIcon = null;
      this.selectedLocationType = null;
    } else {
      this.selectedLocationIcon = iconType;
      this.selectedLocationType = iconType;
    }
    this.locationIconClicked.emit(iconType);
  }

  closeDataPanel(): void {
    this.selectedLocationIcon = null;
  }

  onQuickAction(category: string, action: string): void {
    if (this.demoBlock.block()) { return; }
    const propertyName = this._propertyName;
    const initialMessages: Record<string, Record<string, string>> = {
      weather: {
        'snow-removal': `I need help with snow removal at ${propertyName}.`
      },
      pests: {
        'pest-removal': `I need pest control services at ${propertyName}.`
      },
      utilities: {
        'report-outage': `I need to report a utility outage at ${propertyName}.`
      },
      trash: {
        'large-item': `I need to schedule a large item pickup at ${propertyName}.`,
        'other-pickup': `I need to schedule a special pickup at ${propertyName}.`
      },
      ordinances: {
        'request-permit': `I need help requesting a permit for ${propertyName}.`
      }
    };
    const initialMessage = initialMessages[category]?.[action] || `I need help with ${action.replace(/-/g, ' ')} at ${propertyName}.`;
    this.closeDataPanel();
    this.quickActionClicked.emit({ category, action, initialMessage });
  }

  toggleTimelineMenu(): void {
    this.timelineMenuOpen = !this.timelineMenuOpen;
  }

  onTimelineAction(action: string): void {
    console.log(`Timeline action: ${action}`);
    this.timelineMenuOpen = false;
  }

  getSelectedIconTitle(): string {
    const titles: Record<string, string> = {
      weather: 'Weather',
      pests: 'Pests',
      utilities: 'Utilities',
      trash: 'Trash',
      ordinances: 'Ordinances'
    };
    return titles[this.selectedLocationIcon || ''] || '';
  }

  hasUtilitiesData(): boolean {
    const utility = this.locationData?.attributes?.utility?.data;
    if (!utility) { return false; }
    return !!(utility.electric || utility.water || utility.internet?.length);
  }

  getWeatherIcon(): string {
    const conditions = this.locationData?.attributes?.weather?.data?.conditions?.toLowerCase();
    if (!conditions) { return 'wb_sunny'; }
    if (conditions.includes('rain')) { return 'water_drop'; }
    if (conditions.includes('snow')) { return 'ac_unit'; }
    if (conditions.includes('cloud')) { return 'cloud'; }
    if (conditions.includes('fog') || conditions.includes('mist')) { return 'blur_on'; }
    if (conditions.includes('storm')) { return 'thunderstorm'; }
    return 'wb_sunny';
  }

  getWeatherLabel(): string {
    const weather = this.locationData?.attributes?.weather?.data;
    if (!weather) { return 'Weather'; }
    if (weather.temperature !== undefined) {
      return `${Math.round(weather.temperature)}°`;
    }
    return weather.conditions || 'Weather';
  }

  formatNumber(value: number | undefined): string {
    if (value === undefined) { return ''; }
    return value.toLocaleString();
  }

  hasOrdinancesData(): boolean {
    const data = this.propertyCodesData?.data;
    if (!data) { return false; }
    return !!(data.leaf_blower || data.water || data.business || data.str || data.property || data.parking);
  }

  getOrdinancesSummary(): { category: string; summary: string; icon: string }[] {
    const data = this.propertyCodesData?.data;
    if (!data) { return []; }
    const summaries: { category: string; summary: string; icon: string }[] = [];
    if (data.leaf_blower) {
      const lb = data.leaf_blower;
      const parts: string[] = [];
      if (lb.gas_banned !== undefined) {
        parts.push(lb.gas_banned ? 'Gas banned' : 'Gas not banned');
      }
      if (lb.electric_allowed) { parts.push('electric allowed'); }
      if (lb.allowed_start_weekday && lb.allowed_end) {
        parts.push(`${lb.allowed_start_weekday}-${lb.allowed_end} weekdays`);
      }
      if (lb.allowed_start_weekend && lb.allowed_end) {
        parts.push(`${lb.allowed_start_weekend}-${lb.allowed_end} weekends`);
      }
      if (lb.electric_decibel_limit) { parts.push(`${lb.electric_decibel_limit}dB limit`); }
      if (parts.length) {
        summaries.push({ category: 'Leaf Blower', summary: parts.join(', '), icon: 'air' });
      }
    }
    if (data.water) {
      const w = data.water;
      const parts: string[] = [];
      if (w.drought_stage === 'none' || w.state_drought_status === 'none') {
        parts.push('No current drought restrictions');
      } else if (w.drought_stage) {
        parts.push(`Drought stage: ${w.drought_stage}`);
      }
      if (w.pressure_washing_allowed !== undefined) {
        const pw = w.pressure_washing_allowed ? 'Pressure washing allowed' : 'Pressure washing not allowed';
        if (w.pressure_washing_recapture_required) {
          parts.push(`${pw} but recapture required`);
        } else {
          parts.push(pw);
        }
      }
      if (w.water_authority_name) { parts.push(w.water_authority_name); }
      if (parts.length) {
        summaries.push({ category: 'Water', summary: parts.join(', '), icon: 'water_drop' });
      }
    }
    if (data.business) {
      const b = data.business;
      const parts: string[] = [];
      if (b.business_license_required) { parts.push('License required'); }
      if (b.contractor_license_required_landscaping) { parts.push('Contractor license needed for landscaping'); }
      if (b.workers_comp_required) { parts.push('Workers comp required'); }
      if (parts.length) {
        summaries.push({ category: 'Business', summary: parts.join(', '), icon: 'business' });
      }
    }
    if (data.str) {
      const s = data.str;
      const parts: string[] = [];
      if (s.str_allowed === false) {
        parts.push(`Short-term rentals not allowed in ${this.propertyCodesData?.location?.city || 'this city'}`);
      } else if (s.str_allowed && s.str_permit_required) {
        parts.push('STR allowed with permit');
      } else if (s.str_allowed) {
        parts.push('Short-term rentals allowed');
      }
      if (parts.length) {
        summaries.push({ category: 'STR', summary: parts.join(', '), icon: 'home' });
      }
    }
    if (data.property) {
      const p = data.property;
      const parts: string[] = [];
      if (p.pool_fence_required) {
        const height = p.pool_fence_height_min_ft ? ` (${p.pool_fence_height_min_ft}ft min)` : '';
        parts.push(`Pool fence required${height}`);
      }
      if (p.smoke_detector_required || p.co_detector_required) {
        const detectors = [
          p.smoke_detector_required ? 'smoke' : '',
          p.co_detector_required ? 'CO' : ''
        ].filter(Boolean).join('/');
        parts.push(`${detectors} detectors required`);
      }
      if (p.fence_permit_required) { parts.push('Fence permit required'); }
      if (parts.length) {
        summaries.push({ category: 'Property', summary: parts.join(', '), icon: 'house' });
      }
    }
    if (data.parking) {
      const pk = data.parking;
      const parts: string[] = [];
      if (pk.commercial_vehicle_overnight_allowed === false) {
        parts.push('No commercial vehicles overnight');
      }
      if (pk.trailer_street_parking_hours) {
        parts.push(`${pk.trailer_street_parking_hours}hr limit for trailers/RVs`);
      }
      if (parts.length) {
        summaries.push({ category: 'Parking', summary: parts.join(', '), icon: 'local_parking' });
      }
    }
    return summaries;
  }

  getLocationModalData(): any {
    if (!this.selectedLocationType) { return null; }
    if (this.selectedLocationType === 'ordinances') {
      return {
        ordinances: this.getOrdinancesSummary()
      };
    }
    if (this.selectedLocationType === 'weather') {
      return this.locationData?.attributes?.weather?.data || null;
    }
    if (this.selectedLocationType === 'utilities') {
      return this.locationData?.attributes?.utility?.data || null;
    }
    if (this.selectedLocationType === 'trash') {
      return this.locationData?.attributes?.utility?.data?.trash || null;
    }
    return null;
  }

  onLocationModalClose(): void {
    this.selectedLocationType = null;
  }

  onLocationModalQuickAction(event: QuickActionEvent): void {
    this.onQuickAction(event.category, event.action);
  }

  updateActionDockButtonsData(): void {
    this.actionDockButtons = [
      { id: 'weather', icon: this.getWeatherIcon(), hasData: !!this.locationData?.attributes?.weather?.data },
      { id: 'pests', icon: 'pest_control', hasData: false },
      { id: 'utilities', icon: 'bolt', hasData: this.hasUtilitiesData() },
      { id: 'trash', icon: 'local_shipping', hasData: !!this.locationData?.attributes?.utility?.data?.trash },
      { id: 'ordinances', icon: 'gavel', hasData: this.hasOrdinancesData() }
    ];
  }
}
